import { useEffect, useState, type ReactNode } from "react";
import { AlertTriangle, Check, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  auxFollowsMain, auxiliaryModels, auxiliaryTitle, botShares, deleteCustomEndpoint, emptyEndpointDraft, endpointDraft,
  probeCustomEndpoint, providerMatches, saveCustomEndpoint, saveToolKeys, selectToolBackend, selectToolModel, setAuxiliaryModel,
  setToolsetEnabled, toolBackends, toolModels, toolsets,
  type AuxiliaryModels, type AuxiliaryTask, type CustomEndpoint, type CustomEndpointDraft, type EndpointProbe, type ModelProvider,
  type ToolBackend, type ToolBackends, type ToolModel, type ToolModels, type ToolsetSummary,
} from "@timibot/core";
import { useApp } from "../../context";
import { Dialog, PillButton, SearchField, SectionTitle, Sheet, Spinner } from "../../ui/components";
import { ModelServiceNames } from "./serviceNames";
import "./chat.css";

// 模型页的进阶配置（对应 iOS 的 ModelServicesViews.swift），和 Hermes 桌面端设置页一一对应（都是 Hermes 原生接口）：
// 自定义接口（OpenAI 兼容地址）、辅助模型（看图、压缩…）、生图 / 生视频后端。

/** 模型页里的“下一页”：压进同一个弹窗，左上角“返回” */
export interface Nav {
  push(title: string, node: ReactNode): void;
  pop(): void;
}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

// MARK: 共用小组件

/** 一行“名字 … 当前值 ›” */
function ValueRow({ title, value, dimmed, onClick }: { title: string; value: string; dimmed?: boolean; onClick: () => void }) {
  return (
    <button className="svc-row" onClick={onClick}>
      <span className="t-subheadline">{title}</span>
      <span className="row" style={{ gap: 8, minWidth: 0 }}>
        <span className={`t-subheadline nowrap ${dimmed ? "t-3" : "t-2"}`}>{value}</span>
        <ChevronRight size={14} className="t-3" />
      </span>
    </button>
  );
}

/** 可选列表的一行：选中的打勾、浅灰底 */
function ChoiceRow({ title, detail, selected, onClick }: { title: string; detail?: string; selected: boolean; onClick: () => void }) {
  return (
    <button className={`model-row${selected ? " current" : ""}`} onClick={onClick}>
      <span className="col" style={{ minWidth: 0, gap: 2 }}>
        <span className="t-subheadline nowrap">{title}</span>
        {detail ? <span className="t-caption t-2 nowrap">{detail}</span> : null}
      </span>
      {selected && <Check size={15} strokeWidth={2.6} />}
    </button>
  );
}

function ErrorLine({ text }: { text?: string }) {
  return text ? <div className="t-footnote t-danger model-error">{text}</div> : null;
}

// MARK: 自定义接口

const API_MODES: [string, string][] = [["", "自动识别"], ["chat_completions", "Chat Completions"], ["codex_responses", "Responses"], ["anthropic_messages", "Anthropic Messages"]];

/** 新建或修改一个自定义接口：名称、地址、Key，测试连接后从接口提供的模型里选一个 */
export function CustomEndpointSheet({ open, existing, profile, onClose, onSaved }: {
  open: boolean;
  /** 为空表示新建 */
  existing?: CustomEndpoint;
  profile?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const app = useApp();
  const [draft, setDraft] = useState<CustomEndpointDraft>(emptyEndpointDraft());
  const [contextText, setContextText] = useState("");
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState<EndpointProbe>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setDraft(existing ? endpointDraft(existing) : emptyEndpointDraft());
    setContextText(existing?.context_length !== undefined ? String(existing.context_length) : "");
    setProbe(undefined);
    setError(undefined);
    setPicking(false);
  }, [open, existing]);

  const set = (patch: Partial<CustomEndpointDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const trimmedURL = draft.baseURL.trim();
  const canSave = !!trimmedURL && !!draft.model.trim() && !!draft.name.trim() && !saving;
  const knownModels = probe?.models?.length ? probe.models : draft.models;

  const runProbe = async () => {
    if (!app.api) return;
    setProbing(true);
    try {
      const d = { ...draft, baseURL: trimmedURL };
      const r = await probeCustomEndpoint(app.api, d);
      setProbe(r);
      // 接口实际能用的地址（例如自动补上了 /v1）
      if (r.resolved_base_url) d.baseURL = r.resolved_base_url;
      if (r.models?.length) {
        d.models = r.models;
        if (!d.model || !r.models.includes(d.model)) d.model = r.models[0];
      }
      if (!d.name) { try { d.name = new URL(d.baseURL).hostname; } catch { /* 地址不完整就不填 */ } }
      setDraft(d);
      setError(undefined);
    } catch (e) {
      setError(message(e));
    } finally {
      setProbing(false);
    }
  };

  const save = async () => {
    if (!app.api || !canSave) return;
    setSaving(true);
    const n = Number.parseInt(contextText.trim(), 10);
    const d: CustomEndpointDraft = {
      ...draft, baseURL: trimmedURL, name: draft.name.trim(), model: draft.model.trim(), apiKey: draft.apiKey.trim(),
      contextLength: Number.isFinite(n) ? n : undefined,
    };
    try {
      await saveCustomEndpoint(app.api, d, profile);
      app.showToast(existing ? "已保存" : `已添加 ${d.name}`);
      onSaved();
      onClose();
    } catch (e) {
      setError(message(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!app.api || !existing) return;
    try {
      await deleteCustomEndpoint(app.api, existing.id, profile);
      onSaved();
      onClose();
    } catch (e) {
      setError(message(e));
    }
  };

  const shownModels = knownModels.filter((m) => !search || m.toLowerCase().includes(search.toLowerCase()));

  return (
    <Sheet open={open} onClose={onClose}
      title={picking ? "选择模型" : existing ? existing.name : "自定义接口"}
      leading={picking
        ? <button className="sheet-done row" onClick={() => setPicking(false)}><ChevronLeft size={18} />返回</button>
        : <button className="sheet-done" onClick={onClose}>取消</button>}
      trailing={picking ? undefined : <button className="sheet-done semibold" disabled={!canSave} onClick={() => void save()}>{saving ? "保存中…" : "保存"}</button>}>
      {picking ? (
        <div className="model-list">
          {knownModels.length > 8 && <div className="model-search"><SearchField placeholder="搜索模型" value={search} onChange={setSearch} /></div>}
          {shownModels.map((m) => <ChoiceRow key={m} title={m} selected={m === draft.model} onClick={() => { set({ model: m }); setPicking(false); }} />)}
        </div>
      ) : (
        <div className="form">
          <Field title="名称"><input className="form-inline" value={draft.name} placeholder="例如 我的中转" onChange={(e) => set({ name: e.target.value })} /></Field>
          <Field title="接口地址">
            <input className="form-inline" type="url" inputMode="url" value={draft.baseURL} placeholder="https://example.com/v1"
              autoCapitalize="off" autoCorrect="off" spellCheck={false} onChange={(e) => set({ baseURL: e.target.value })} />
          </Field>
          <Field title="API Key">
            <input className="form-inline mono" type="password" value={draft.apiKey} autoComplete="off"
              placeholder={existing?.has_api_key ? (existing.api_key_preview ?? "已保存") : "可不填"} onChange={(e) => set({ apiKey: e.target.value })} />
          </Field>
          <div className="row" style={{ gap: 10, minHeight: 32 }}>
            <PillButton title={probing ? "连接中…" : "测试连接"} disabled={!trimmedURL || probing} onClick={() => void runProbe()} />
            {probe && (probe.ok
              ? <span className="row t-footnote" style={{ gap: 4, color: "var(--success)" }}><CheckCircle2 size={14} />已连接 · {probe.models?.length ?? 0} 个模型</span>
              : <span className="row t-footnote clamp2" style={{ gap: 4, color: probe.reachable === false ? "var(--danger)" : "var(--warning)" }}>
                <AlertTriangle size={14} style={{ flexShrink: 0 }} />{probe.message || "连接失败"}
              </span>)}
          </div>
          <Field title="模型">
            {knownModels.length === 0
              ? <input className="form-inline" value={draft.model} placeholder="例如 gpt-5.4" autoCapitalize="off" autoCorrect="off" spellCheck={false}
                onChange={(e) => set({ model: e.target.value })} />
              : <button className="value-line" onClick={() => { setSearch(""); setPicking(true); }}>
                <span className={`nowrap ${draft.model ? "" : "t-3"}`}>{draft.model || "选择模型"}</span><ChevronRight size={14} className="t-3" />
              </button>}
          </Field>
          <Field title="高级">
            <label className="value-line">
              <span>接口类型</span>
              <select className="select" value={draft.apiMode} onChange={(e) => set({ apiMode: e.target.value })}>
                {API_MODES.map(([id, t]) => <option key={id} value={id}>{t}</option>)}
              </select>
            </label>
            <label className="value-line">
              <span>上下文长度</span>
              <input className="t-2" style={{ textAlign: "right", maxWidth: 140, fontSize: 15 }} inputMode="numeric" placeholder="自动"
                value={contextText} onChange={(e) => setContextText(e.target.value.replace(/\D/g, ""))} />
            </label>
          </Field>
          {error && <span className="t-footnote t-danger">{error}</span>}
          {existing && <button className="t-subheadline t-danger" style={{ padding: 8 }} onClick={() => setConfirmDelete(true)}>删除这个接口</button>}
        </div>
      )}
      <Dialog open={confirmDelete} title="删除这个接口？" onClose={() => setConfirmDelete(false)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "删除", role: "destructive", onClick: () => void remove() }]} />
    </Sheet>
  );
}

function Field({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="form-group">
      <span className="form-label">{title}</span>
      <div className="form-card">{children}</div>
    </div>
  );
}

// MARK: 辅助模型

/** 常用的三项排前面 */
const COMMON_AUX = ["vision", "compression", "title_generation"];

/** 辅助任务（看图、压缩上下文、起标题…）各自用哪个模型，默认跟随主模型 */
export function AuxiliaryModelsPage({ profile, providers, nav }: { profile?: string; providers: ModelProvider[]; nav: Nav }) {
  const app = useApp();
  const [aux, setAux] = useState<AuxiliaryModels>();
  const [error, setError] = useState<string>();

  const load = async () => {
    if (!app.api) return;
    try { setAux(await auxiliaryModels(app.api, profile)); setError(undefined); } catch (e) { setError(message(e)); }
  };
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const assign = async (task: AuxiliaryTask, provider: string, model: string) => {
    if (!app.api) return;
    try { await setAuxiliaryModel(app.api, task.task, provider, model, profile); await load(); } catch (e) { setError(message(e)); }
  };

  const row = (t: AuxiliaryTask) => (
    <ValueRow key={t.task} title={auxiliaryTitle(t)} value={auxFollowsMain(t) ? "跟随主模型" : (t.model || t.provider)} dimmed={auxFollowsMain(t)}
      onClick={() => nav.push(auxiliaryTitle(t), <AuxiliaryChoiceList task={t} providers={providers} onPick={async (p, m) => { nav.pop(); await assign(t, p, m); }} />)} />
  );

  const tasks = aux?.tasks ?? [];
  const main = tasks.filter((t) => COMMON_AUX.includes(t.task)).sort((a, b) => COMMON_AUX.indexOf(a.task) - COMMON_AUX.indexOf(b.task));
  const others = tasks.filter((t) => !COMMON_AUX.includes(t.task));
  return (
    <div className="value-rows">
      <ErrorLine text={error} />
      {!aux && !error && <div className="center-pad"><Spinner /></div>}
      {main.map(row)}
      {others.length > 0 && <><div style={{ paddingTop: 14 }}><SectionTitle title="其他" /></div>{others.map(row)}</>}
    </div>
  );
}

/** 给一项辅助任务选模型：最上面“跟随主模型”，下面按服务列出已配置的模型 */
function AuxiliaryChoiceList({ task, providers, onPick }: { task: AuxiliaryTask; providers: ModelProvider[]; onPick: (provider: string, model: string) => void }) {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();
  return (
    <div className="model-list">
      <div className="model-search"><SearchField placeholder="搜索模型" value={search} onChange={setSearch} /></div>
      {!search && <ChoiceRow title="跟随主模型" selected={auxFollowsMain(task)} onClick={() => onPick("auto", "")} />}
      {providers.map((p) => {
        const models = (p.models ?? []).filter((m) => !q || m.toLowerCase().includes(q));
        if (!models.length) return null;
        return (
          <section key={p.slug} className="model-section">
            <SectionTitle title={ModelServiceNames.title(p)} />
            {models.map((m) => (
              <ChoiceRow key={m} title={m} selected={!auxFollowsMain(task) && providerMatches(p, task.provider) && m === task.model} onClick={() => onPick(p.slug, m)} />
            ))}
          </section>
        );
      })}
    </div>
  );
}

// MARK: 生图 / 生视频

const MEDIA_KINDS: [string, string][] = [["image_gen", "生图"], ["video_gen", "生视频"]];

/** Hermes 的 image_gen / video_gen 工具：开关、用哪个后端（填它的 Key）、用哪个模型 */
export function MediaGenerationPage({ profile, nav }: { profile?: string; nav: Nav }) {
  const app = useApp();
  const [sets, setSets] = useState<ToolsetSummary[]>();
  const [error, setError] = useState<string>();
  /** 这个 Bot 跟随主账号：主账号改了，connector 会把这里同步成一样的 */
  const [follows, setFollows] = useState(false);

  const load = async () => {
    if (!app.api) return;
    try { setSets(await toolsets(app.api, profile)); setError(undefined); } catch (e) { setError(message(e)); }
  };
  useEffect(() => {
    void load();
    if (app.api && profile && profile !== "default") botShares(app.api, profile).then((s) => setFollows(s.media === "follow"), () => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="value-rows">
      {follows && <div className="t-footnote t-2" style={{ padding: "0 20px 6px" }}>跟随主账号：主账号的生图、生视频设置改了，这个 Bot 会自动跟着改。</div>}
      <ErrorLine text={error} />
      {MEDIA_KINDS.map(([name, title]) => {
        const summary = sets?.find((t) => t.name === name);
        return summary ? <ToolsetBlock key={name} toolset={name} title={title} summary={summary} profile={profile} nav={nav} onChange={load} /> : null;
      })}
      {!sets && !error && <div className="center-pad"><Spinner /></div>}
    </div>
  );
}

/** 一个工具（生图或生视频）：标题 + 开关，下面是后端和模型 */
function ToolsetBlock({ toolset, title, summary, profile, nav, onChange }: {
  toolset: string; title: string; summary: ToolsetSummary; profile?: string; nav: Nav; onChange: () => Promise<void>;
}) {
  const app = useApp();
  const [enabled, setEnabled] = useState(summary.enabled ?? false);
  const [backends, setBackends] = useState<ToolBackends>();
  const [models, setModels] = useState<ToolModels>();
  const [keyTarget, setKeyTarget] = useState<ToolBackend>();
  const [error, setError] = useState<string>();

  // 需要 Nous 订阅登录的后端先不放（App 里没有网页登录）
  const usable = (backends?.providers ?? []).filter((b) => b.requires_nous_auth !== true);
  const active = usable.find((b) => b.is_active);

  const load = async () => {
    if (!app.api) return;
    try {
      setBackends(await toolBackends(app.api, toolset, profile));
      setModels(await toolModels(app.api, toolset, undefined, profile).catch(() => undefined));
      setError(undefined);
    } catch (e) {
      setError(message(e));
    }
  };
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (on: boolean) => {
    setEnabled(on);
    try { await setToolsetEnabled(app.api!, toolset, on, profile); await onChange(); } catch (e) { setEnabled(!on); setError(message(e)); }
  };
  const select = async (b: ToolBackend) => {
    try { await selectToolBackend(app.api!, toolset, b.name, profile); await load(); } catch (e) { setError(message(e)); }
  };
  const saveKeys = async (env: Record<string, string>, b: ToolBackend) => {
    try { await saveToolKeys(app.api!, toolset, env, profile); await select(b); } catch (e) { setError(message(e)); }
  };
  const selectModel = async (model: string) => {
    try { await selectToolModel(app.api!, toolset, model, active?.name, profile); await load(); } catch (e) { setError(message(e)); }
  };

  const list = models?.models ?? [];
  return (
    <div className="toolset-block">
      <div className="toolset-head">
        <span className="t-headline">{title}</span>
        <input type="checkbox" className="switch" aria-label={title} checked={enabled} onChange={(e) => void toggle(e.target.checked)} />
      </div>
      {enabled && (
        <ValueRow title="后端" value={active?.name ?? "未选择"} dimmed={!active}
          onClick={() => nav.push(title, <BackendChoiceList backends={usable} onPick={(b) => { nav.pop(); if (b.status === "needs_keys") setKeyTarget(b); else void select(b); }} />)} />
      )}
      {enabled && list.length > 0 && (
        <ValueRow title="模型" value={list.find((m) => m.id === models?.current)?.display ?? models?.current ?? "默认"}
          onClick={() => nav.push("模型", <ToolModelList models={list} current={models?.current} onPick={(m) => { nav.pop(); void selectModel(m); }} />)} />
      )}
      <ErrorLine text={error} />
      <ToolKeySheet backend={keyTarget} onClose={() => setKeyTarget(undefined)} onSave={(env) => { if (keyTarget) void saveKeys(env, keyTarget); }} />
    </div>
  );
}

function backendDetail(b: ToolBackend): string {
  // 正在用的就不提示了：Hermes 对订阅授权类后端的就绪检查不准，用得好好的也会报 needs_auth
  if (b.is_active) return "";
  if (b.status === "needs_keys") return "需要 API Key";
  if (b.status === "needs_auth" || b.status === "needs_setup") return "需要在主机上配置";
  return b.badge ?? "";
}

function BackendChoiceList({ backends, onPick }: { backends: ToolBackend[]; onPick: (b: ToolBackend) => void }) {
  return (
    <div className="model-list">
      {backends.map((b) => <ChoiceRow key={b.name} title={b.name} detail={backendDetail(b)} selected={b.is_active === true} onClick={() => onPick(b)} />)}
    </div>
  );
}

function ToolModelList({ models, current, onPick }: { models: ToolModel[]; current?: string; onPick: (id: string) => void }) {
  return (
    <div className="model-list">
      {models.map((m) => (
        <ChoiceRow key={m.id} title={m.display ?? m.id} detail={[m.price, m.speed].filter(Boolean).join(" · ")} selected={m.id === current} onClick={() => onPick(m.id)} />
      ))}
    </div>
  );
}

/** 填一个生图 / 生视频后端需要的 Key（可能不止一个） */
function ToolKeySheet({ backend, onClose, onSave }: { backend?: ToolBackend; onClose: () => void; onSave: (env: Record<string, string>) => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => { if (backend) setValues({}); }, [backend]);
  const vars = backend?.env_vars ?? [];
  const missing = vars.some((v) => v.is_set !== true && !(values[v.key] ?? "").trim());
  const save = () => {
    const env = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));
    onSave(env);
    onClose();
  };
  return (
    <Sheet open={backend !== undefined} onClose={onClose} size="medium" title={backend?.name}
      leading={<button className="sheet-done" onClick={onClose}>取消</button>}
      trailing={<button className="sheet-done semibold" disabled={missing} onClick={save}>保存</button>}>
      <div className="form">
        {vars.map((v) => (
          <div key={v.key} className="form-group">
            <span className="form-label">{v.key}</span>
            <div className="form-card">
              <input className="form-inline mono" type="password" autoComplete="off" value={values[v.key] ?? ""}
                placeholder={v.is_set ? "已保存" : (v.prompt ?? v.key)} onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))} />
            </div>
            {v.url && <a className="t-footnote t-brand form-hint" href={v.url} target="_blank" rel="noreferrer">获取 Key</a>}
          </div>
        ))}
      </div>
    </Sheet>
  );
}
